"use client";

import { useState } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { Input } from "@/components/ui/Input";
import { cn } from "@/components/ui/cn";
import { useRecipientSearch } from "@/lib/hooks/useRecipientSearch";
import { useDismissable } from "@/lib/hooks/useDismissable";
import type { UserSummary } from "@/lib/api/messages";
import { personName } from "./participants";

/** MAX_PARTICIPANTS (20) minus the sender. */
const MAX_OTHERS = 19;

/**
 * The same shape the server accepts on `CreateInviteBody.email`. It is a
 * "does this look like an address" test and nothing more — the server is what
 * decides, and it answers with a Danish sentence when it disagrees.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;

/**
 * An extra row, offered under the search results when the term is an ADDRESS.
 *
 * The picker searches PEOPLE — `useRecipientSearch` is `?messageableBy=me`,
 * which is the shared-group scope and cannot match a stranger's e-mail — so
 * without this, typing an address into it dead-ends at "Ingen personer
 * fundet". Group invitations need both roads from one field (OSO-441): a
 * person you already share a group with, or an address for somebody the
 * platform has never heard of.
 *
 * OPTIONAL, and absent for DMs: there is no such thing as messaging an address.
 */
export interface EmailOption {
  /** The row's label, e.g. `(e) => \`Send invitation til ${e}\``. */
  label: (email: string) => string;
  onSelect: (email: string) => void;
}

export function ParticipantPicker({
  value,
  onChange,
  excludeIds = [],
  maxOthers = MAX_OTHERS,
  autoFocus = false,
  placeholder = "Søg efter en person…",
  emailOption,
}: {
  value: UserSummary[];
  onChange: (next: UserSummary[]) => void;
  /** People already in the conversation — never offered as results. */
  excludeIds?: number[];
  maxOthers?: number;
  autoFocus?: boolean;
  placeholder?: string;
  /** See `EmailOption`. Omit it and the picker offers people only. */
  emailOption?: EmailOption;
}) {
  const [term, setTerm] = useState("");
  const [limitHit, setLimitHit] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable<HTMLDivElement>(open, () => setOpen(false), {
    escape: false,
  });
  const { results, truncated } = useRecipientSearch(term);

  // BY ID, not by handle: `username` is off the wire (O-21), and ten accounts
  // carried a numeric username equal to a different user's id (O-21a), so a
  // case-folded string compare was never an identity test.
  const excluded = new Set([...excludeIds, ...value.map((v) => v.id)]);
  const visible = results.filter((u) => !excluded.has(u.id));

  // The address arm, when the caller wants one and the term looks like one.
  const typedEmail =
    emailOption && EMAIL_RE.test(term.trim()) ? term.trim().toLowerCase() : null;

  function pickEmail(email: string) {
    setLimitHit(false);
    setTerm("");
    setOpen(false);
    emailOption?.onSelect(email);
  }

  function add(user: UserSummary) {
    if (value.length >= maxOthers) {
      setLimitHit(true);
      return;
    }
    setLimitHit(false);
    onChange([...value, user]);
    setTerm("");
    setOpen(false);
  }

  function remove(userId: number) {
    setLimitHit(false);
    onChange(value.filter((v) => v.id !== userId));
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-surface px-2 py-2 focus-within:ring-2 focus-within:ring-indigo-500">
        {value.map((user) => (
          <span
            key={user.id}
            className="flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-muted text-sm text-gray-800 dark:text-gray-200"
          >
            <Avatar url={user.avatar.small} name={personName(user)} size="sm" />
            <span className="max-w-[160px] truncate">{personName(user)}</span>
            <button
              type="button"
              onClick={() => remove(user.id)}
              aria-label={`Fjern ${personName(user)}`}
              className="w-4 h-4 rounded-full flex items-center justify-center text-subtle-foreground hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <Input
          type="text"
          value={term}
          autoFocus={autoFocus}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && term === "" && value.length > 0) {
              remove(value[value.length - 1].id);
            }
            if (e.key === "Enter") {
              e.preventDefault();
              if (visible.length > 0) add(visible[0]);
              // A person always wins: the address row is the fallback for a
              // term the people search cannot answer.
              else if (typedEmail) pickEmail(typedEmail);
            }
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[140px] bg-transparent px-1 py-1 text-sm text-foreground placeholder:text-subtle-foreground border-0 rounded-none focus:outline-none focus:ring-0"
        />
      </div>

      {limitHit && (
        <p className="text-xs text-destructive mt-1">
          Du kan højst have 20 deltagere i en samtale
        </p>
      )}

      {open && term.trim().length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-surface rounded-xl border border-border shadow-lg z-50 py-1 max-h-72 overflow-y-auto">
          {visible.length === 0 && !typedEmail ? (
            <p className="px-4 py-3 text-sm text-subtle-foreground text-center">
              Ingen personer fundet
            </p>
          ) : (
            visible.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => add(user)}
                className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-surface-2 transition-colors"
              >
                <Avatar url={user.avatar.small} name={personName(user)} size="sm" />
                <p className="min-w-0 text-sm text-foreground truncate">
                  {personName(user)}
                </p>
              </button>
            ))
          )}

          {/* UNDER the people, never above them: the search is scoped to the
              groups you share, so a name match is the better answer whenever
              there is one, and the address is what you fall back to for
              somebody the platform does not know yet. */}
          {typedEmail && emailOption && (
            <button
              type="button"
              onClick={() => pickEmail(typedEmail)}
              className={cn(
                "flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-surface-2 transition-colors",
                visible.length > 0 && "border-t border-border",
              )}
            >
              <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-subtle-foreground shrink-0">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 6L2 7" />
                </svg>
              </span>
              <span className="min-w-0 text-sm text-foreground truncate">
                {emailOption.label(typedEmail)}
              </span>
            </button>
          )}

          {/* The route answered `hasMore` at its own maximum: there are more
              matches than this list can be, and `excluded` has narrowed it
              further. Saying so is the difference between "they are not in
              here" and "keep typing" — without it the person simply is not on
              the list and there is no way to reach them. */}
          {truncated && (
            <p className="px-4 py-2 text-xs text-subtle-foreground text-center border-t border-border">
              Der er flere resultater — skriv mere af navnet
            </p>
          )}
        </div>
      )}
    </div>
  );
}
